import { LoadingDots } from "PFComponents/loading_dots";
import { TooltipProps } from "PFComponents/tooltip/tooltip";
import React, { ReactNode, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { hideAll } from "tippy.js";

import { BookingBarContainerProps } from "./booking_bar_container";
import { useBookingBarContext } from "./booking_bar_provider";
import BookingTooltipContent from "./booking_tooltip_content";

interface UseBookingBarTooltip extends Pick<BookingBarContainerProps, "isDisabled" | "error"> {
  hover: boolean;
}

interface UseBookingBarTooltipReturn {
  tooltipContent: ReactNode;
  tooltipProps: Pick<TooltipProps, "enableVisible" | "visible" | "sticky" | "disabled" | "onShow">;
}

export const useBookingBarTooltip = ({
  isDisabled,
  error,
  hover
}: UseBookingBarTooltip): UseBookingBarTooltipReturn => {
  const { booking, isLoading, draggingMode, draggingStartDate, draggingEndDate, category, isSplittable } =
    useBookingBarContext();
  const { t } = useTranslation("bookingModule");

  useEffect(() => {
    if (!hover) {
      hideAll();
    }
  }, [hover]);

  const getTooltipContent = () => {
    if (error) {
      return <BookingTooltipContent title={error} />;
    } else if (!category) {
      return <BookingTooltipContent title={t("bookings.tooltip.noCategory")} />;
    } else if (isLoading) {
      return <LoadingDots />;
    } else if (draggingMode) {
      return <BookingTooltipContent start={draggingStartDate} end={draggingEndDate} />;
    } else if (booking) {
      return (
        <BookingTooltipContent
          title={booking.title}
          start={booking.start_date}
          end={booking.end_date}
          categoryName={category?.display_as}
        />
      );
    } else {
      return t("bookings.tooltip.getError");
    }
  };

  return {
    tooltipContent: getTooltipContent(),
    tooltipProps: {
      enableVisible: !!draggingMode,
      visible: draggingMode === "start_end",
      sticky: true,
      disabled: isDisabled || isSplittable,
      onShow: () => {
        hideAll();
      }
    }
  };
};
